/**
 * Inversion Mock Repository - Repositorio simulado para inversiones
 * Para desarrollo y pruebas sin Firebase
 */

import { Inversion } from '../../../shared/types';
import { LoggerService } from '../../../shared/services/logger.service';

export class InversionMockRepository {
  private inversiones: Inversion[] = [];
  private listeners: { [userId: string]: ((inversiones: Inversion[]) => void)[] } = {};

  constructor() {
    // Datos mock iniciales
    this.initializeMockData();
  }

  private initializeMockData(): void {
    const mockInversiones: Inversion[] = [
      {
        id: 'mock-inversion-1',
        userId: 'mock-user-1',
        monto: 100000,
        descripcion: 'Fondo de inversión conservador',
        tipo: 'Fondo Mutuo',
        fecha: new Date('2024-01-10'),
        createdAt: new Date('2024-01-10'),
        updatedAt: new Date('2024-01-10')
      },
      {
        id: 'mock-inversion-2',
        userId: 'mock-user-1',
        monto: 50000,
        descripcion: 'Acciones de empresas tecnológicas',
        tipo: 'Acciones',
        fecha: new Date('2024-01-15'),
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-01-15')
      },
      {
        id: 'mock-inversion-3',
        userId: 'mock-user-1',
        monto: 200000,
        descripcion: 'Bonos del gobierno',
        tipo: 'Bonos',
        fecha: new Date('2024-01-20'),
        createdAt: new Date('2024-01-20'),
        updatedAt: new Date('2024-01-20')
      },
      {
        id: 'mock-inversion-4',
        userId: 'mock-user-1',
        monto: 75000,
        descripcion: 'Criptomonedas diversificadas',
        tipo: 'Criptomonedas',
        fecha: new Date('2024-01-25'),
        createdAt: new Date('2024-01-25'),
        updatedAt: new Date('2024-01-25')
      }
    ];

    this.inversiones = mockInversiones;
  }

  subscribeToInversiones(userId: string, callback: (inversiones: Inversion[]) => void): () => void {
    if (!this.listeners[userId]) {
      this.listeners[userId] = [];
    }
    
    this.listeners[userId].push(callback);
    
    // Notificar datos iniciales
    const userInversiones = this.inversiones.filter(inversion => inversion.userId === userId);
    callback(userInversiones);
    
    LoggerService.debug('Mock inversiones subscription added', 'InversionMockRepository', { userId });
    
    // Retornar función de unsubscribe
    return () => {
      const index = this.listeners[userId].indexOf(callback);
      if (index > -1) {
        this.listeners[userId].splice(index, 1);
      }
      LoggerService.debug('Mock inversiones subscription removed', 'InversionMockRepository', { userId });
    };
  }

  async create(userId: string, inversion: Omit<Inversion, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<void> {
    LoggerService.info('Mock inversion creation', 'InversionMockRepository', { userId, inversion });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newInversion: Inversion = {
      ...inversion,
      id: `mock-inversion-${Date.now()}`,
      userId,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    this.inversiones.push(newInversion);
    this.notifyListeners(userId);
    
    LoggerService.logUserAction('mock_inversion_created', 'InversionMockRepository', { userId, inversionId: newInversion.id });
  }

  async update(userId: string, id: string, inversion: Partial<Omit<Inversion, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    LoggerService.info('Mock inversion update', 'InversionMockRepository', { userId, id, inversion });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.inversiones.findIndex(i => i.id === id && i.userId === userId);
    if (index === -1) {
      throw new Error('Inversión no encontrada');
    }
    
    this.inversiones[index] = {
      ...this.inversiones[index],
      ...inversion,
      updatedAt: new Date()
    };
    
    this.notifyListeners(userId);
    
    LoggerService.logUserAction('mock_inversion_updated', 'InversionMockRepository', { userId, inversionId: id });
  }

  async delete(userId: string, id: string): Promise<void> {
    LoggerService.info('Mock inversion deletion', 'InversionMockRepository', { userId, id });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.inversiones.findIndex(i => i.id === id && i.userId === userId);
    if (index === -1) {
      throw new Error('Inversión no encontrada');
    }
    
    this.inversiones.splice(index, 1);
    this.notifyListeners(userId);
    
    LoggerService.logUserAction('mock_inversion_deleted', 'InversionMockRepository', { userId, inversionId: id });
  }

  async getByType(userId: string, tipo: string): Promise<Inversion[]> {
    LoggerService.debug('Mock get by type', 'InversionMockRepository', { userId, tipo });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return this.inversiones.filter(inversion => 
      inversion.userId === userId && inversion.tipo === tipo
    );
  }

  async getByDateRange(userId: string, startDate: Date, endDate: Date): Promise<Inversion[]> {
    LoggerService.debug('Mock get by date range', 'InversionMockRepository', { userId, startDate, endDate });
    
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 200));
    
    return this.inversiones.filter(inversion => 
      inversion.userId === userId &&
      inversion.fecha >= startDate &&
      inversion.fecha <= endDate
    );
  }

  private notifyListeners(userId: string): void {
    if (this.listeners[userId]) {
      const userInversiones = this.inversiones.filter(inversion => inversion.userId === userId);
      this.listeners[userId].forEach(callback => callback(userInversiones));
    }
  }
}
